export const ARABOM_BASE_STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";
export const ARABOM_MAX_ZOOM = 13;
export const KOREAN_LABEL_FIELD = [
  "coalesce",
  ["get", "name:ko"],
  ["get", "name:nonlatin"],
  ["get", "name"],
] as const;

export type MapStyleLayer = {
  id: string;
  type: string;
  "source-layer"?: string;
  maxzoom?: number;
  layout?: Record<string, unknown>;
  paint?: Record<string, unknown>;
  [key: string]: unknown;
};

export type MapStyleDocument = {
  version: 8;
  layers: MapStyleLayer[];
  sources?: Record<string, { type?: string; [key: string]: unknown }>;
  metadata?: Record<string, unknown>;
  [key: string]: unknown;
};

const grayscaleByPaintProperty: Readonly<Record<string, string>> = Object.freeze({
  "background-color": "#f5f6f3",
  "fill-color": "#e7e9e5",
  "fill-extrusion-color": "#e1e4df",
  "line-color": "#aeb5ae",
  "circle-color": "#707a72",
  "text-color": "#465049",
  "text-halo-color": "#fafbf8",
  "icon-color": "#5f6962",
  "icon-halo-color": "#fafbf8",
});

function noisyLayer(layer: MapStyleLayer): boolean {
  const identity = `${layer.id} ${layer["source-layer"] ?? ""}`.toLowerCase();
  return /(^|[-_\s])(poi|housenumber|building)([-_\s]|$)/u.test(identity);
}

export function deriveArabomMapStyle(input: MapStyleDocument): MapStyleDocument {
  if (input.version !== 8 || !Array.isArray(input.layers)) {
    throw new Error("OpenFreeMap style is not a MapLibre v8 document");
  }
  const clone = structuredClone(input);
  clone.layers = clone.layers
    .filter((layer) => !noisyLayer(layer))
    .map((layer) => {
      const paint = { ...(layer.paint ?? {}) };
      for (const [property, value] of Object.entries(grayscaleByPaintProperty)) {
        if (property in paint) paint[property] = value;
      }
      const layout = { ...(layer.layout ?? {}) };
      if (layer.type === "symbol" && "text-field" in layout) {
        layout["text-field"] = KOREAN_LABEL_FIELD;
      }
      return {
        ...layer,
        maxzoom: Math.min(layer.maxzoom ?? ARABOM_MAX_ZOOM + 1, ARABOM_MAX_ZOOM + 1),
        ...(Object.keys(layout).length ? { layout } : {}),
        ...(Object.keys(paint).length ? { paint } : {}),
      };
    });
  clone.metadata = {
    ...(clone.metadata ?? {}),
    "arabom:derivedFrom": ARABOM_BASE_STYLE_URL,
    "arabom:maxZoom": ARABOM_MAX_ZOOM,
    "arabom:noiseLayersRemoved": ["poi", "housenumber", "building"],
  };
  return clone;
}

/**
 * Return the style's raster base layer rather than treating MapLibre's global
 * `load` event as the only readiness signal. A vector source can continue
 * parsing while the visible raster backdrop has already rendered.
 */
export function findArabomBackgroundSource(style: MapStyleDocument): string | null {
  return Object.entries(style.sources ?? {}).find(([, source]) => source.type === "raster")?.[0] ?? null;
}

export async function loadArabomMapStyle(
  fetcher: typeof fetch = fetch,
  signal: AbortSignal = AbortSignal.timeout(8_000),
): Promise<MapStyleDocument> {
  const response = await fetcher(ARABOM_BASE_STYLE_URL, { signal });
  if (!response.ok) throw new Error(`OpenFreeMap style HTTP ${response.status}`);
  return deriveArabomMapStyle(await response.json() as MapStyleDocument);
}
